//------- import libraries
import { Form, Formik } from "formik";
import { connect } from "react-redux";
import { Col, Modal, Row } from "react-bootstrap";
import { useEffect, useRef, useState } from "react";

//------- import custom reuseable components
import {
  Button,
  CheckboxField,
  ProductTagList,
  CategoriesDropdown,
  ProductModalHeader,
  AddInventoryProductPrices,
  AddEditInventoryModalProductProperties,
  AddEditInventoryModalPricesHistoryStats,
} from "../../../../shared/components";

//------ import actions
import {
  inventoryActions,
  shopifyActions,
  systemActions,
} from "../../../../redux/actions";

//------ import validation schemas
import { addNewIventoryValidationSchema } from "../../../../shared/validationSchema/validationSchema";

//------ import system enums
import {
  PRODUCT_CONDITIONS_ENUMS,
  TRADING_CARD_CATEGORIES_ENUMS,
} from "../../../../system/globalEnums";

//------- import icons
import { SidebarToggleIcon } from "../../../../assets/icons/iconsProvider";

//------ import utility functions
import {
  customToast,
  handleTagList,
  isSystemProduct,
  isSerialNumberUnique,
  getProductSubCategory,
  handleEditBatchConditionChange,
  batchEditChangePriceBaseOnTags,
  getAddInventoryPartialInitialValues,
  getDefaultShopifySettings,
  handleBeautifyMarketPrice,
  findInventoryProductDetail,
  handleTransformClientInventoryJsonToDbFormat,
} from "../../../../shared/utility";

//------- import constants
import {
  toastType,
  EMPTY_STRING,
  toastMessages,
  inventoryConstants,
  buttonNameConstants,
  buttonTypeConstants,
  categoryDropdownConstants,
  toggleSwitchButtonConstants,
  INITIAL_VALUE_TYPES,
  INVENTORY_MODAL_TYPES,
} from "../../../../constants";

const BatchProductEdit = (props) => {
  //-------Props
  const {
    shopify,
    addToCart,
    inventory,
    currentStore,
    tradeDiscount,
    activateSpinner,
    deactivateSpinner,
    addBatchInventory,
    editProductObject,
    getShopifyMappingsById,
    isBatchProductEditModal,
    priceChartingProductInfo,
    setPriceChartingProductInfo,
    toggleIsBatchProductEditModal,
    emptyPriceChartingProductObject,
    setProductShopifySettings,
  } = props;

  //-------inventory reducer
  const { productTags, productShopifySettings } = inventory;

  //-------tradeDiscount Reducer
  const { markupPresets, tradeinMargin } = tradeDiscount;

  const { shopifySettings } = shopify;

  //------UseRef
  const hiddenFileInput = useRef(null);

  //-------useState
  const [selectedTags, setSelectedTags] = useState([]);
  const [productDetail, setProductDetail] = useState("");
  const [addSelectedTags, setAddSelectedTags] = useState([]);
  const [productUrl, setProductUrl] = useState(EMPTY_STRING);
  const [isInStoreQuantity, setIsInStoreQuantity] = useState(false);
  const [productDataObject, setProductDataObject] = useState(editProductObject);
  const [isFirstRender, setIsFirstRender] = useState(true); //this becomes false when we change the condition or tags of product
  const [productTagsList, setProductTagsList] = useState(
    handleTagList(
      productTags,
      productDataObject?.genre,
      editProductObject?.productCondition,
      inventoryConstants.NINTENDO_CATEGORIES.includes(
        productDataObject?.consoleName
      ) ||
        inventoryConstants.SKYLANDER_CATEGORIES.includes(
          productDataObject?.consoleName
        )
        ? editProductObject?.productCondition ===
          PRODUCT_CONDITIONS_ENUMS.COMPLETE
          ? inventoryConstants.PRODUCT_TAGS.MISSING_MANUAL
          : inventoryConstants.PRODUCT_TAGS.WITH_MANUAL
        : ""
    ) || []
  );
  const [productType, setProductType] = useState(
    editProductObject?.productType
  );
  const [productCondition, setProductCondition] = useState(
    editProductObject?.productCondition
      ? {
          value: editProductObject?.productCondition,
          label: editProductObject?.productCondition,
        }
      : {}
  );
  const batchProductEditRef = useRef();

  //-------Initial Values
  const initialValues = {
    box:
      productCondition?.value === PRODUCT_CONDITIONS_ENUMS.COMPLETE &&
      selectedTags?.includes(inventoryConstants.PRODUCT_TAGS.WITH_BOX),
    manual:
      (productCondition?.value === PRODUCT_CONDITIONS_ENUMS.COMPLETE &&
        !selectedTags?.includes(
          inventoryConstants.PRODUCT_TAGS.MISSING_MANUAL
        )) ||
      (productCondition?.value === PRODUCT_CONDITIONS_ENUMS.LOOSE &&
        selectedTags?.includes(inventoryConstants.PRODUCT_TAGS.WITH_MANUAL)),
    cables:
      (productCondition?.value === PRODUCT_CONDITIONS_ENUMS.COMPLETE ||
        productCondition?.value === PRODUCT_CONDITIONS_ENUMS.LOOSE) &&
      !selectedTags?.includes(inventoryConstants.PRODUCT_TAGS.MISSING_WIRES),

    controller:
      (productCondition?.value === PRODUCT_CONDITIONS_ENUMS.COMPLETE ||
        productCondition?.value === PRODUCT_CONDITIONS_ENUMS.LOOSE) &&
      !selectedTags?.includes(
        inventoryConstants.PRODUCT_TAGS.MISSING_CONTROLLER
      ),
    costOfGoods: isFirstRender
      ? editProductObject?.costOfGoods
      : productDataObject?.costOfGoods,
    inStockPrice: productDataObject?.inStockPrice || EMPTY_STRING,
    ...getAddInventoryPartialInitialValues(
      {
        ...productDataObject,
        quantity: editProductObject?.inStockQuantity || 1,
        onlineUnitPurchasePrice: isFirstRender
          ? editProductObject?.onlineUnitPurchasePrice
          : productDataObject?.onlineUnitPurchasePrice,
        onlineUnitSellPrice: isFirstRender
          ? editProductObject?.onlineUnitSellPrice ||
            editProductObject?.price?.onlineUnitSellPrice ||
            0
          : productDataObject?.onlineUnitSellPrice ||
            productDataObject?.price?.onlineUnitSellPrice ||
            Number(productDataObject.inStockPrice || 0) ||
            0,
        isSeparateQuantity: editProductObject?.isSeparateQuantity ?? false,
      },
      INITIAL_VALUE_TYPES.EDIT_INVENTORY_WHILE_ADDING,
      batchProductEditRef
    ),
  };
  //-------Functions
  //-------Handle Hide Modal
  const handleHideModal = () => {
    emptyPriceChartingProductObject();
    toggleIsBatchProductEditModal();
  };

  //-------Handle Replace Image Button Click
  const handleReplaceButtonClick = () => {
    hiddenFileInput.current.click();
  };

  //------- toggle InStore Quantity
  const toggleIsInStoreQuantity = () => {
    setIsInStoreQuantity((prev) => !prev);
  };

  //-------Handle Submit
  const handleSubmit = (values) => {
    const batchProducts = addBatchInventory?.filter(
      (product) =>
        product?.productId === editProductObject?.productId &&
        product?.id !== editProductObject?.id
    );

    if (!productDataObject?.productName) {
      customToast("Please select some product", toastType.ERROR);
    } else if (
      isSystemProduct(getProductSubCategory(productDataObject?.genre)) &&
      !isSerialNumberUnique(values.serialNumber, [
        ...(editProductObject?.productsAvailableInInventory || []),
        ...(batchProducts || []),
      ])
    ) {
      customToast(
        toastMessages.PRODUCT_SERIAL_NUMBER_SHOULD_BE_UNIQUE,
        toastType.ERROR
      );
    } else if (values?.serialNumber && values.quantity > 1) {
      customToast(toastMessages.SYSTEM_QUANTITY_MUST_BE_1, toastType.ERROR);
      return;
    } else {
      const sendToData = {
        ...productDataObject,
        ...values,
        id: productDataObject?.id,
        productId: productDataObject.productId,
        productName: productDataObject.productName,
        consoleName: productDataObject.consoleName,
        marketPrice: productDataObject.marketPrice,
        suggestedSellPrice: productDataObject.suggestedSellPrice,
        costOfGoods: values.costOfGoods,
        inStockPrice: values.inStockPrice,
        inStockQuantity: values.quantity,
        skuNumber: 0,
        gameStopBuyPrice: productDataObject.gameStopBuyPrice,
        averageBuyPrice: productDataObject.averageBuyPrice,
        averageSellPrice: productDataObject.averageSellPrice,
        maxBuyPrice: productDataObject.maxBuyPrice,
        imgUrl: productDataObject?.imgUrl,
        productCondition: productCondition?.value,
        cardRarity: values.cardRarity || EMPTY_STRING,
        tags: productDataObject.tags?.map((tag) => tag),
        serialNumber: values.serialNumber,
      };
      addToCart(sendToData);
      handleHideModal();
    }
  };

  //-------handle tag select or unselect
  const onSelectTagsChange = (selectedTags) => {
    const selectedTagList = selectedTags.map((tag) => tag);
    const selectedTagListTags = selectedTags.map((tag) => tag.label);
    batchEditChangePriceBaseOnTags(
      selectedTagList,
      productDataObject,
      setProductDataObject,
      productCondition,
      productType,
      tradeinMargin,
      markupPresets
    );

    // commenting it because changing tags cause infinite rendering and after commenting it seems to work just fine
    // setSelectedTags(selectedTagListTags);
    // setAddSelectedTags(selectedTagList);
  };

  //-------Handle Shopfiy In-Store & Online Quantity Check Change
  const handleStoreAndOnlineQuantityChange = (value) => {
    setProductDataObject((prevState) => ({
      ...prevState,
      isOnlineQuantity: value,
    }));
  };

  //-------useEffects
  useEffect(() => {
    if (productCondition?.value) {
      findInventoryProductDetail(
        currentStore,
        productCondition?.value,
        productType,
        markupPresets,
        tradeinMargin,
        selectedTags,
        priceChartingProductInfo?.productMetaData?.cardRarity,
        productDataObject,
        editProductObject?.productsAvailableInInventory || [],
        setProductDataObject,
        priceChartingProductInfo,
        setPriceChartingProductInfo,
        isFirstRender
          ? handleTransformClientInventoryJsonToDbFormat(editProductObject)
          : null,
        {
          shopifySettings,
          getShopifyMappingsById,
          productShopifySettings,
          setProductShopifySettings,
        },
        INVENTORY_MODAL_TYPES.EDIT_FROM_ADD_BATCH_INVENTORY_MODAL
      );
    }
  }, [
    selectedTags,
    shopifySettings,
    productCondition,
    priceChartingProductInfo?.productMetaData?.cardRarity,
  ]);
  console.log(editProductObject);

  useEffect(() => {
    if (productShopifySettings?.marginObject?.id) {
      setProductDataObject((prev) => ({
        ...prev,
        shopifySettingId: productShopifySettings?.marginObject?.id,
      }));
    }
  }, [productShopifySettings]);

  useEffect(() => {
    setProductShopifySettings(addBatchInventory[0]?.shopifySettingsObject);
  }, []);

  //-------Return batch Edit Component
  return (
    <Modal
      show={isBatchProductEditModal}
      size="xl"
      onHide={handleHideModal}
      animation={true}
      centered
      backdrop="static"
      className={`add-inventory-modal ${productUrl ? "iframe-opened" : ""}`}
    >
      <Formik
        initialValues={initialValues}
        innerRef={batchProductEditRef}
        enableReinitialize
        onSubmit={handleSubmit}
        validate={(values) =>
          addNewIventoryValidationSchema(
            values,
            currentStore?.[
              toggleSwitchButtonConstants.IS_SERIAL_NUMBER_REQUIRED_INVENTORY
            ] &&
              getProductSubCategory(productDataObject?.genre) ===
                TRADING_CARD_CATEGORIES_ENUMS.SYSTEM
          )
        }
      >
        {({ values, setFieldValue }) => (
          <Form>
            <Modal.Header
              closeButton
              className="add-inventory-modal-header  pt-0 pb-1"
            >
              <Modal.Title>
                <span className="add-inventory-modal-header-name">
                  Edit Batch Inventory
                </span>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="add-inventory-modal-body d-flex ">
              <Row className={`m-0 ${productUrl ? "w-50" : "w-100"}`}>
                <Col xs={12} className="add-inventory-hero-wrapper px-0">
                  <Row className="m-0">
                    {/* Product image, meta data and Open Iframe Buttons */}
                    <ProductModalHeader
                      values={values}
                      productTags={productTags}
                      selectedTags={selectedTags}
                      setProductUrl={setProductUrl}
                      activateSpinner={activateSpinner}
                      hiddenFileInput={hiddenFileInput}
                      productDataObject={productDataObject}
                      deactivateSpinner={deactivateSpinner}
                      setProductDataObject={setProductDataObject}
                      handleReplaceButtonClick={handleReplaceButtonClick}
                      priceChartingProductInfo={priceChartingProductInfo}
                      isEditModal={true}
                      handleConditionChange={handleEditBatchConditionChange}
                      setAddSelectedTags={setAddSelectedTags}
                      markupPresets={markupPresets}
                      tradeinMargin={tradeinMargin}
                      setProductTagsList={setProductTagsList}
                      setSelectedTags={setSelectedTags}
                      setFieldValue={setFieldValue}
                      setConditionList={(e) => {
                        setProductDataObject({
                          ...productDataObject,
                          conditionList: e,
                          productConditionList: e,
                        });
                      }}
                      productCondition={productCondition}
                      setProductCondition={setProductCondition}
                      setPriceChartingProductInfo={setPriceChartingProductInfo}
                      isConvertToUsd={true}
                      setIsFirstRender={setIsFirstRender}
                    />
                    {/* <Col md={12} className="gap-3 mt-3 pt-2">
                      <AddEditInventoryModalProductProperties
                        values={values}
                        setFieldValue={setFieldValue}
                        priceChartingProductInfoComponent={productDataObject}
                        setPriceChartingProductInfoComponent={
                          setProductDataObject
                        }
                        conditionComponent={{
                          setConditionList: (e) => {
                            setProductDataObject({
                              ...productDataObject,
                              conditionList: e,
                              productConditionList: e,
                            });
                          },
                          productCondition: productCondition,
                          setProductCondition: setProductCondition,
                          component: (
                            <CategoriesDropdown
                              options={
                                productDataObject?.productConditionList || []
                              }
                              handleChange={(selectedOption) =>
                                handleEditBatchConditionChange(
                                  markupPresets,
                                  selectedOption,
                                  setProductCondition,
                                  productTags,
                                  setProductTagsList,
                                  setProductDataObject,
                                  productDataObject,
                                  setSelectedTags,
                                  tradeinMargin,
                                  setAddSelectedTags,
                                  undefined,
                                  setPriceChartingProductInfo
                                )
                              }
                              noOptionsMessage="No Condition Found"
                              placeholder="Condition"
                              SelectIconImage={
                                categoryDropdownConstants.INVENTORY_LIST_ICON
                              }
                              isClearable={false}
                              className="add-inventory-dropdowns-category condition-dropdown"
                              value={productCondition}
                            />
                          ),
                        }}
                        // raritiesComponent={{
                        //   handleRaritiesChange: (selectedOption) => {
                        //     handleRaritiesChange(
                        //       selectedOption,
                        //       setSelectedRarity,
                        //       setProductDataObject,
                        //       productDataObject,
                        //       tradeinMargin,
                        //       markupPresets
                        //     );
                        //   },
                        // }}
                      />
                    </Col> */}
                    {productCondition?.value ===
                      PRODUCT_CONDITIONS_ENUMS.COMPLETE ||
                    productCondition?.value ===
                      PRODUCT_CONDITIONS_ENUMS.LOOSE ? (
                      <Col
                        md={12}
                        className="d-flex align-items-center gap-3 my-2"
                      >
                        {productDataObject.genre ===
                        TRADING_CARD_CATEGORIES_ENUMS.SYSTEM ? (
                          productCondition?.value ===
                          PRODUCT_CONDITIONS_ENUMS.COMPLETE ? (
                            <>
                              <CheckboxField name="manual" label="Manual" />
                              {/* <CheckboxField name="box" label="Box" /> */}
                              <CheckboxField name="cables" label="Cables" />
                              <CheckboxField
                                name="controller"
                                label="Controller"
                              />
                            </>
                          ) : productCondition?.value ===
                            PRODUCT_CONDITIONS_ENUMS.LOOSE ? (
                            <>
                              <CheckboxField name="manual" label="Manual" />
                              <CheckboxField name="cables" label="Cables" />
                              <CheckboxField
                                name="controller"
                                label="Controller"
                              />
                            </>
                          ) : (
                            ""
                          )
                        ) : [
                            ...inventoryConstants.NINTENDO_CATEGORIES,
                            ...inventoryConstants.SKYLANDER_CATEGORIES,
                          ].includes(editProductObject?.consoleName) ? (
                          ""
                        ) : (
                          <CheckboxField name="manual" label="Manual" />
                        )}
                      </Col>
                    ) : (
                      ""
                    )}

                    <Col
                      md={12}
                      className="my-2 d-flex align-items-center gap-4"
                    >
                      <ProductTagList
                        setIsFirstRender={setIsFirstRender}
                        className={"align-items-center"}
                        tagsList={productTagsList || []}
                        suggestedArrayList={productTags}
                        onSelectTagsChange={onSelectTagsChange}
                        productDetail={productDataObject}
                        productCondition={productCondition}
                        selectedManual={{
                          isChecked: values.manual,
                          value:
                            productCondition?.value ===
                            PRODUCT_CONDITIONS_ENUMS.COMPLETE
                              ? inventoryConstants.PRODUCT_TAGS.MISSING_MANUAL
                              : productCondition?.value ===
                                PRODUCT_CONDITIONS_ENUMS.LOOSE
                              ? inventoryConstants.PRODUCT_TAGS.WITH_MANUAL
                              : "",
                        }}
                        selectedWires={{
                          isChecked: values.cables,
                          value:
                            PRODUCT_CONDITIONS_ENUMS.COMPLETE ||
                            productCondition?.value ===
                              PRODUCT_CONDITIONS_ENUMS.LOOSE
                              ? inventoryConstants.PRODUCT_TAGS.MISSING_WIRES
                              : "",
                        }}
                        selectedController={{
                          isChecked: values.controller,
                          value:
                            PRODUCT_CONDITIONS_ENUMS.COMPLETE ||
                            productCondition?.value ===
                              PRODUCT_CONDITIONS_ENUMS.LOOSE
                              ? inventoryConstants.PRODUCT_TAGS
                                  .MISSING_CONTROLLER
                              : "",
                        }}
                      />
                    </Col>
                  </Row>
                </Col>

                <Col xs={12} className="m-0 p-0 mt-4">
                  <Row className="m-0">
                    <Col md={4} className="px-0">
                      <AddEditInventoryModalPricesHistoryStats
                        isBatchProduct={true}
                        productDataObject={productDataObject}
                        setProductDataObject={setProductDataObject}
                        propertyNameOfStatCardsToHide={["totalUnitsSold"]}
                        isShowInStoreOnlineQuantityCheck={true}
                        handleStoreAndOnlineQuantityChange={
                          handleStoreAndOnlineQuantityChange
                        }
                      />
                    </Col>
                    <Col md={8}>
                      <Row className="m-0 h-100">
                        <AddInventoryProductPrices
                          values={values}
                          productTags={productTags}
                          productType={productType}
                          selectedTags={selectedTags}
                          currentStore={currentStore}
                          setFieldValue={setFieldValue}
                          markupPresets={markupPresets}
                          marginTypeObject={
                            productDataObject?.tradeInMarginTypeObject
                          }
                          isInStoreQuantity={isInStoreQuantity}
                          productDataObject={productDataObject}
                          toggleIsInStoreQuantity={toggleIsInStoreQuantity}
                          priceChartingProductInfo={priceChartingProductInfo}
                          productUrl={productUrl}
                        />
                        <Col md={12} className="d-flex align-items-end gap-2">
                          <Button
                            label={buttonNameConstants.CANCEL}
                            className="w-100"
                            type="button"
                            handleClick={() => handleHideModal()}
                            buttonType={buttonTypeConstants.GHOST_BUTTON}
                          />
                          <Button
                            label={buttonNameConstants.SAVE}
                            className="w-100"
                            type="submit"
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
              {productUrl && (
                <Row className="m-0 w-50">
                  <Col md={12}>
                    {productUrl && (
                      <iframe
                        title="Product Iframe"
                        src={productUrl}
                        className="iframe-website"
                      />
                    )}
                  </Col>
                </Row>
              )}
            </Modal.Body>
          </Form>
        )}
      </Formik>
      {productUrl && (
        <button
          type="button"
          onClick={() => {
            setProductUrl("");
          }}
          className="iframe-toogle-button"
        >
          <SidebarToggleIcon className={"iframe-toggle-icon"} />
        </button>
      )}
    </Modal>
  );
};

//-------Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  inventory: state.inventory,
  shopify: state.shopify,
  tradeDiscount: state.tradeDiscount,
  currentStore: state.store.currentStore,
  addBatchInventory: state.inventory.addBatchInventory,
  priceChartingProductInfo: state.inventory.priceChartingProductInfo,
});

//-------Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  activateSpinner: (data) => dispatch(systemActions.activateSpinner(data)),
  deactivateSpinner: (data) => dispatch(systemActions.deactivateSpinner(data)),
  emptyPriceChartingProductObject: () =>
    dispatch(inventoryActions.emptyPriceChartingProductObject()),
  setPriceChartingProductInfo: (data) =>
    dispatch(inventoryActions.setPriceChartingProductInfo(data)),
  setProductShopifySettings: (data) =>
    dispatch(inventoryActions.setProductShopifySettings(data)),
  getShopifyMappingsById: (storeId, id) =>
    dispatch(shopifyActions.getShopifyMappingsById(storeId, id)),
});

//-------Export AddNewUser Component
export default connect(mapStateToProps, mapDispatchToProps)(BatchProductEdit);
